import configKeys from '../../consts/config/configKeys';
import IAP_DEVICE_MODELS from '../../consts/iap/iapDeviceModels';
import log from '../logger/log';

const consentsMapping = (response) => {
    const mappedConsents = response?.items?.map((consent) => ({
        consentFlags: consent?.consentFlags,
        code: consent?.code,
        name: consent?.name,
        type: consent?.type,
        isConsentVisible: consent?.isConsentVisible,
        position: consent?.position,
        updatedAtTs: consent?.updatedAtTs,
        texts: consent?.texts.map((text) => ({
            languageCode: text?.languageCode,
            content: text?.content,
            title: text?.title,
        })),
    }));

    return mappedConsents?.sort((x, y) => x.position - y.position); // 0, 1, 2...
};

const settingsConfigMapping = (settings) =>
    // { "settingName": "name", "code": "CODE", "value": true} ==> "CODE": "value"
    settings?.reduce((collector, setting) => {
        const {code, value} = setting;

        collector[code] = value;

        return collector;
    }, {});

// { "name": "name", "value": "value"} ==> "name": "value"
const customFeaturesConfigMapping = (customFeatures) =>
    customFeatures?.reduce((collector, feature) => {
        const {name, value} = feature;

        collector[name] = value;

        return collector;
    }, {});

// ['MERCURY_L_LCD_C', 'SATURN_X'] ==> ['MERCURY_L_LCD_C']
const knownDeviceModelsMapping = (deviceModels, code) =>
    deviceModels?.filter((deviceModel) => {
        if (IAP_DEVICE_MODELS[deviceModel]) return true;

        log.warn(`iapMappingService: Unknown device model "${deviceModel}" in deviceFeatures.${code}`);

        return false;
    });

// [{code: 'DEVICE_LOCK', deviceModels: ['MERCURY_L_LCD_C'], isEnabled: true}] ==> {DEVICE_LOCK: ['MERCURY_L_LCD_C']}
export const deviceFeaturesConfigMapping = (deviceFeatures) =>
    deviceFeatures?.reduce((collector, deviceFeature) => {
        const {code, deviceModels, isEnabled} = deviceFeature;

        if (!code) return collector;

        collector[code] = isEnabled ? knownDeviceModelsMapping(deviceModels, code) : [];

        return collector;
    }, {});

const generalSettingsConfigMapping = (settings) =>
    settings?.reduce((collector, {code}) => {
        if (code) collector[code] = false;

        return collector;
    }, {});

const toFeatureCode = (name) => name.trim().replace(/\s+/g, '_').toUpperCase();

const generalDeviceFeaturesConfigMapping = (deviceFeatures) =>
    deviceFeatures?.reduce((collector, {name}) => {
        if (name) collector[toFeatureCode(name)] = [];

        return collector;
    }, {});

const uwmRowMapping = ({feature, uwm, isDefault}) => ({feature, uwm, isDefault: Boolean(isDefault)});

export const selectUwmModules = (uwmRows) => {
    const features = [...new Set(uwmRows.map(({feature}) => feature))];

    return features.map((feature) => {
        const rows = uwmRows.filter((row) => row.feature === feature);
        const {uwm} = rows.find(({isDefault}) => isDefault) ?? rows[0];

        return {feature, uwm};
    });
};

const uwmConfigMapping = (uwmConfig) => uwmConfig && selectUwmModules(uwmConfig.map(uwmRowMapping));

const userGroupsConfigMapping = (userGroupsConfig) =>
    userGroupsConfig?.map(({appType, group, features}) => ({appType, group, features}));

const targetGroupsConfigMapping = (targetGroupsConfig) =>
    targetGroupsConfig?.map(({appType, targetGroup, uwmConfig}) => ({
        appType,
        targetGroup,
        uwmConfig: uwmConfig?.map(uwmRowMapping),
    }));

const marketConfigDataMapping = (configResponse) => {
    const mappedConfig = {};

    for (const configKey of Object.keys(configResponse)) {
        const configItem = configResponse[configKey];

        switch (configKey) {
            case configKeys.SETTINGS:
                mappedConfig[configKey] = settingsConfigMapping(configItem);
                break;
            case configKeys.CUSTOM_FEATURES:
                mappedConfig[configKey] = customFeaturesConfigMapping(configItem);
                break;
            case configKeys.DEVICE_FEATURES:
                mappedConfig[configKey] = deviceFeaturesConfigMapping(configItem);
                break;
            case configKeys.UWM_CONFIG:
                mappedConfig[configKey] = uwmConfigMapping(configItem);
                break;
            default:
                break;
        }
    }

    return mappedConfig;
};

const generalConfigDataMapping = (configResponse) => {
    const mappedConfig = {};

    for (const configKey of Object.keys(configResponse)) {
        const configItem = configResponse[configKey];

        switch (configKey) {
            case configKeys.SETTINGS:
                mappedConfig[configKey] = generalSettingsConfigMapping(configItem);
                break;
            case configKeys.DEVICE_FEATURES:
                mappedConfig[configKey] = generalDeviceFeaturesConfigMapping(configItem);
                break;
            case configKeys.MARKET_LOCALIZATIONS:
                mappedConfig[configKey] = configItem;
                break;
            case configKeys.UWM_CONFIG:
                mappedConfig[configKey] = uwmConfigMapping(configItem);
                break;
            default:
                break;
        }
    }

    return mappedConfig;
};

export default {
    consentsMapping,
    marketConfigDataMapping,
    generalConfigDataMapping,
    userGroupsConfigMapping,
    targetGroupsConfigMapping,
};
